import type { PlannerTemplateFragment } from "@/graphql";
import toRRuleState from "@/helpers/rrules/toRRuleState";
import { toDate, toUnix } from "@/stories/utils/dates";
import { nanoid } from "nanoid";
import type { PlannerFormItem, PlannerItem } from "@/types";
import {
  Approval_State,
  Container_Type,
  Plannertask_Assignee_Type,
  Profile_Type,
  type PlannerSubtaskInput,
  type PlannerTaskAssigneeInput,
  type PlannerTaskCreateInput,
  type PlannerTaskEditInput,
} from "@/graphql/generated-types";
import type { IFileState } from "@/interfaces";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";
import { format } from "date-fns";

const toImageOrPictogram = (images: IFileState[] | undefined) => {
  let image: PlannerTaskCreateInput["image"] = null;
  let pictogramId: string | null = null;
  if (images) {
    const [firstItem] = images.length ? images : [undefined];
    if (firstItem?.externalSource === "pictogram" && firstItem.id) {
      pictogramId = firstItem.id;
    } else if (firstItem) {
      image = {
        body: firstItem.base64,
        container: Container_Type.Planner,
        title: firstItem.name,
        url: firstItem.url,
      };
    }
  }

  return { image, pictogramId };
};

const toPlannerTaskCreateInput = (
  form: PlannerFormItem,
): PlannerTaskCreateInput => {
  const { image, pictogramId } = toImageOrPictogram(form.images);
  let rset: PlannerTaskCreateInput["rset"] = undefined;

  if (form.rruleState) {
    rset = {
      excludedDates: [],
      rule: toRRuleInput(form.rruleState),
    };
  }

  // Assignees
  const { residents, departments, employees } = form;
  const assignees: PlannerTaskAssigneeInput[] = employees.map((id) => {
    return {
      id: id,
      type: Plannertask_Assignee_Type.Employee,
    };
  });

  if (residents.length > 0) {
    const [resident] = residents;
    assignees.push({ id: resident, type: Plannertask_Assignee_Type.Resident });
  }

  if (departments.length > 0) {
    const [department] = departments;
    assignees.push({
      id: department,
      type: Plannertask_Assignee_Type.Department,
    });
  }

  const subtasks: PlannerSubtaskInput[] = form.subtasks.map(
    (item, sortOrder) => {
      const { image, pictogramId } = toImageOrPictogram(
        item.image ? [item.image] : [],
      );
      return {
        id: item.id,
        image,
        pictogramId,
        sortOrder,
        title: item.title,
      };
    },
  );

  return {
    title: form.title,
    end: format(toDate(form.endDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(toDate(form.startDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    pictogramId,
    image,
    categoryId: form.category,
    description: form.description,
    rset,
    isRepeat: !!rset,
    approvalState: Approval_State.Unneeded,
    assignees,
    notificationSettings: form.notificationSettings,
    subtasks,
    visibleForResident: form.visibleForResident,
  };
};

const toPlannerTaskEditInput = (
  form: PlannerFormItem,
): PlannerTaskEditInput => {
  const { image, pictogramId } = toImageOrPictogram(form.images);
  let rset: PlannerTaskEditInput["rset"] = undefined;

  if (form.rruleState) {
    rset = {
      excludedDates: [],
      rule: toRRuleInput(form.rruleState),
    };
  }

  // Assignees
  const { residents, departments, employees } = form;
  const assignees: PlannerTaskAssigneeInput[] = employees.map((id) => {
    return {
      id: id,
      type: Plannertask_Assignee_Type.Employee,
    };
  });

  if (residents.length > 0) {
    const [resident] = residents;
    assignees.push({ id: resident, type: Plannertask_Assignee_Type.Resident });
  }

  if (departments.length > 0) {
    const [department] = departments;
    assignees.push({
      id: department,
      type: Plannertask_Assignee_Type.Department,
    });
  }

  const subtasks: PlannerSubtaskInput[] = form.subtasks.map(
    (item, sortOrder) => {
      const { image, pictogramId } = toImageOrPictogram(
        item.image ? [item.image] : [],
      );
      return {
        id: item.id,
        image,
        pictogramId,
        sortOrder,
        title: item.title,
      };
    },
  );

  return {
    title: form.title,
    end: format(toDate(form.endDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    start: format(toDate(form.startDate), "yyyy-MM-dd'T'HH:mm:ssXXX"),
    pictogramId,
    image,
    categoryId: form.category,
    description: form.description,
    rset,
    isRepeat: !!rset,
    approvalState: Approval_State.Unneeded,
    assignees,
    notificationSettings: [...form.notificationSettings],
    subtasks,
    visibleForResident: form.visibleForResident,
  };
};

const fromTemplateToPlannerFormItem = (
  template: PlannerTemplateFragment,
): Partial<PlannerFormItem> => {
  const nextStartDate = new Date();

  if (template.start) {
    const [hours, minutes] = template.start.split(":");

    if (hours && minutes) {
      nextStartDate.setHours(
        Number.parseInt(hours),
        Number.parseInt(minutes),
        0,
        0,
      );
    }
  }

  const nextEndDate = new Date(nextStartDate);

  if (template.duration !== null && template.duration !== undefined) {
    nextEndDate.setMinutes(nextEndDate.getMinutes() + template.duration);
  } else {
    nextEndDate.setHours(nextEndDate.getHours() + 1);
  }

  const imageState = template.pictogram || template.image;

  return {
    type: "create",
    title: template.title,
    category: template.category?.id,
    startDate: toUnix(nextStartDate),
    endDate: toUnix(nextEndDate),
    images: imageState
      ? [
          {
            id: imageState.id,
            name: imageState.description || imageState.id,
            originalName: imageState.description || "",
            base64: imageState.source,
            url: imageState.source,
            type: "image",
            action: "upload",
            ...(template.pictogram
              ? { externalSource: "pictogram" as const }
              : {}),
          },
        ]
      : [],
    imageType: template.pictogram ? "pictogram" : "upload",
    rruleState: template.repeat?.rset
      ? {
          ...toRRuleState(template.repeat.rset),
          startDate: toUnix(nextStartDate),
        }
      : undefined,
    subtasks: (template.subtasks || []).map((subtask) => {
      return {
        id: nanoid(),
        title: subtask.title,
        mode: "edit",
      };
    }),
    validation: undefined,
  };
};

const fromPlannerItemToPlannerFormItem = (
  item: PlannerItem,
): PlannerFormItem => {
  const startDate = toUnix(new Date(item.startDate));
  const endDate = toUnix(new Date(item.endDate));

  const imageState = item.pictogram || item.picture;
  const imageId =
    imageState && "id" in imageState && typeof imageState.id === "string"
      ? imageState.id
      : undefined;

  return {
    type: "edit",
    id: item.id,
    isRepeating: item.__typename === "RepeatingPlannerTask",
    title: item.title,
    category: item.category?.id,
    startDate,
    endDate,
    description: item.description || "",
    employees: item.assignees
      .filter((assignee) => assignee.__typename === "Employee")
      .map((assignee) => assignee.id),
    departments: item.assignees
      .filter((assignee) => assignee.__typename === "Department")
      .map((assignee) => assignee.id),
    residents: item.assignees
      .filter((assignee) => assignee.__typename === "Resident")
      .map((assignee) => assignee.id),
    images: imageState
      ? [
          {
            id: imageId,
            name: imageId || item.title,
            originalName: imageId || "",
            base64: imageState.source,
            url: imageState.source,
            type: "image",
            action: "upload",
            ...(item.pictogram ? { externalSource: "pictogram" as const } : {}),
          },
        ]
      : [],
    imageType: item.pictogram ? "pictogram" : "upload",
    rruleState:
      item.__typename === "RepeatingPlannerTask" && item.repeat?.rset
        ? toRRuleState(item.repeat.rset)
        : undefined,
    notificationSettings: (item.notificationSettings || []).map((item) => {
      return {
        profile: {
          type:
            item.profile.__typename === "Employee"
              ? Profile_Type.Employee
              : Profile_Type.Resident,
          id: item.profile.id,
        },
        reminderFirst: item.reminderFirst,
        reminderFourth: item.reminderFourth,
        reminderSecond: item.reminderSecond,
        reminderThird: item.reminderThird,
        state: item.state,
        triggerWhenAssignmentChange: item.triggerWhenAssignmentChange,
        triggerWhenAssignmentComplete: item.triggerWhenAssignmentComplete,
        triggerWhenAssignmentOverdue: item.triggerWhenAssignmentOverdue,
        triggerWhenNewAssignment: item.triggerWhenNewAssignment,
      };
    }),
    subtasks: (item.subtasks || []).map((subtask) => {
      const subtaskImage = subtask.pictogram || subtask.picture;

      return {
        id: subtask.id,
        title: subtask.title,
        mode: "data",
        image: subtaskImage
          ? {
              id: subtaskImage.id,
              name: subtaskImage.id,
              originalName: subtaskImage.id,
              base64: subtaskImage.source,
              url: subtaskImage.source,
              type: "image",
              action: "upload",
              ...(subtask.pictogram
                ? { externalSource: "pictogram" as const }
                : {}),
            }
          : undefined,
      };
    }),
    validation: undefined,
    visibleForResident: item.visibleForResident || false,
  };
};

export default {
  toPlannerTaskCreateInput,
  toPlannerTaskEditInput,
  fromTemplateToPlannerFormItem,
  fromPlannerItemToPlannerFormItem,
};
